/**
 * Kanonische Feldnamen-Normalisierung.
 *
 * Problem: CSV-Parser und AI-Pipeline geben sym/symOut/symIn aus.
 * FIFO-, Lot- und Transfer-System lesen coin/coinOut/coinIn.
 * Dieser Mismatch führt zu lots[undefined] und stumm fehlschlagenden Transfers.
 *
 * Lösung: ADDITIV — sym/symOut/symIn bleiben erhalten (importValidation.js braucht sie),
 * coin/coinOut/coinIn werden zusätzlich gesetzt. Beide Felder sind danach befüllt.
 *
 * Muss aufgerufen werden NACH dem Parse und VOR transferMatcher/lotSystem/fifo.
 */

/**
 * Kopiert sym→coin, symOut→coinOut, symIn→coinIn (falls coin-Felder fehlen).
 * Macht alle Mengen positiv und normalisiert Symbole auf UPPERCASE.
 *
 * @param {Object} rawTx  Rohes Transaktionsobjekt aus CSV-Normalizer oder AI-Pipeline
 * @returns {Object}  Neues Objekt mit vollständig belegten coin-Feldern
 */
export function normalizeToCanonical(rawTx) {
  const tx = { ...rawTx };

  // ── Feldname-Unifikation: sym → coin ────────────────────────────────────────
  // Beide Felder werden befüllt. sym bleibt für importValidation.js erhalten.
  if (tx.coin    == null) tx.coin    = tx.sym    ?? tx.asset ?? null;
  if (tx.coinIn  == null) tx.coinIn  = tx.symIn  ?? null;
  if (tx.coinOut == null) tx.coinOut = tx.symOut ?? null;

  // ── Symbole: immer UPPERCASE, kein Leerzeichen ─────────────────────────────
  if (tx.coin)    tx.coin    = String(tx.coin).trim().toUpperCase();
  if (tx.coinIn)  tx.coinIn  = String(tx.coinIn).trim().toUpperCase();
  if (tx.coinOut) tx.coinOut = String(tx.coinOut).trim().toUpperCase();
  // sym ebenfalls normalisieren (für importValidation.js-Konsistenz)
  if (tx.sym)    tx.sym    = String(tx.sym).trim().toUpperCase();
  if (tx.symIn)  tx.symIn  = String(tx.symIn).trim().toUpperCase();
  if (tx.symOut) tx.symOut = String(tx.symOut).trim().toUpperCase();

  // ── Beträge: immer positiv (Parser liefern manchmal negative Mengen) ────────
  // AUSNAHME: preis bei futures_pnl/funding_fee ist VORZEICHENBEHAFTET (negativ
  // = Verlust/Kosten, positiv = Gewinn). Absolutieren wuerde die FIFO-Engine
  // dazu bringen, einen Verlust als Gewinn zu verbuchen und neue Lots anzulegen
  // statt zu konsumieren -> Phantom-Bestand (Symptom: 158 USDT statt 1-2 USDT
  // nach Bitget-Futures-Liquidation).
  if (tx.menge    != null) tx.menge    = Math.abs(tx.menge);
  if (tx.mengeIn  != null) tx.mengeIn  = Math.abs(tx.mengeIn);
  if (tx.mengeOut != null) tx.mengeOut = Math.abs(tx.mengeOut);
  if (tx.preis    != null && tx.typ !== 'futures_pnl' && tx.typ !== 'funding_fee') {
    tx.preis = Math.abs(tx.preis);
  }
  if (tx.gebuehr  != null) tx.gebuehr  = Math.abs(tx.gebuehr);

  // ── Fiat-Paar-Swaps zu kauf/verkauf normalisieren ───────────────────────────
  // Aeltere Imports (vor dem Parser-Fix) haben EUR→XLM, EUR→XRP usw. als
  // typ='swap' mit preis=0 in Firestore. Im UI wirkt das wie ein Token-Tausch
  // (TAUSCH-Pille, 0,00 EUR), und Asset-Analyse (Ø Kaufpreis, Cash-In, Cash-Out)
  // erkennt diese TXs nicht als echte Kaeufe/Verkaeufe.
  // -> Beim Read aus Firestore in semantisch korrekten Typ umwandeln.
  // -> Original-Swap-Felder bleiben fuer Edit-Round-Trip erhalten.
  if (tx.typ === 'swap') {
    const inFiat  = NORM_FIAT_SET.has(String(tx.coinIn  || '').toUpperCase());
    const outFiat = NORM_FIAT_SET.has(String(tx.coinOut || '').toUpperCase());
    if (inFiat && !outFiat) {
      // Crypto -> Fiat = Verkauf
      tx.typ   = 'verkauf';
      tx.coin  = tx.coinOut;
      tx.sym   = tx.symOut || tx.coinOut;
      tx.menge = Number(tx.mengeOut) || 0;
      tx.preis = Number(tx.mengeIn)  || 0;
      tx._normalizedFromSwap = true;
    } else if (outFiat && !inFiat) {
      // Fiat -> Crypto = Kauf
      tx.typ   = 'kauf';
      tx.coin  = tx.coinIn;
      tx.sym   = tx.symIn || tx.coinIn;
      tx.menge = Number(tx.mengeIn)  || 0;
      tx.preis = Number(tx.mengeOut) || 0;
      tx._normalizedFromSwap = true;
    }
    // Beide Fiat oder beide Crypto -> bleibt swap.
  }

  // ── Scam-Dust-Erkennung (Address-Poisoning) ─────────────────────────────────
  // On-Chain verifiziert (2026-06-01): winzige „Externe -> Ledger"-Eingaenge wie
  // 0.000001 XRP / 0.000013 ALGO sind KEIN Staking, sondern Address-Poisoning-
  // Angriffe. Beweis via Algorand-Indexer: tx-type=pay, receiver-rewards=0,
  // Sender-Adresse endet auf „…SCAM", note = Impersonation-Warnung im Klartext.
  // Der Angreifer sendet Sekunden nach einer echten Einzahlung, damit seine
  // Adresse in der History neben der echten auftaucht.
  // Der Scam-„note" steht NICHT in der CSV/Firestore — lokal bleibt nur:
  // Eingang OHNE Fiat-Gegenwert + non-oekonomische Menge. Rein Display-seitig
  // (_scamDust): FIFO/Bestand bleiben unberuehrt, die Betraege sind ohnehin
  // mikroskopisch. Manuell klassifizierte / gematchte Transfers sind geschuetzt.
  if (isLikelyScamDust(tx)) tx._scamDust = true;

  return tx;
}

// Eingaenge unter dieser Menge OHNE Fiat-Gegenwert gelten als Dust. Identische
// Schwelle wie der (test-only) Parser-Filter in csvNormalizer.js (parseLedger,
// `dir==='in' && amount<0.01 && !(eurValue>0)`). Unterschied: der dead Parser
// VERWIRFT die Zeile, wir behalten sie und flaggen sie nur (Read-Layer), damit
// sie nachvollziehbar im „Sonstige"-Tab bleibt. Der aktive Parser
// (simpleCsvImporter.js) speichert Dust als einzahlung — hier rueckwirkend
// erkannt, ohne Re-Import. Der Fiat-Gegenwert-Filter (countervalue) schliesst
// teure Coins (BTC/ETH, immer bepreist) aus, sodass die Mengen-Schwelle nicht
// versehentlich einen echten Kleinbetrag eines wertvollen Coins trifft.
const SCAM_DUST_MAX_MENGE = 0.01;

function isLikelyScamDust(tx) {
  if (tx.typ !== 'einzahlung') return false;
  if (tx.userEdited) return false;                        // manuelle Klassifikation gewinnt
  if (tx.linkedTxId || tx.externalTransfer) return false; // echter/bekannter Transfer
  const cv = Number(tx.countervalue);
  if (Number.isFinite(cv) && cv > 0) return false;        // hatte Fiat-Wert bei Zufluss -> real
  const menge = Number(tx.menge);
  return menge > 0 && menge < SCAM_DUST_MAX_MENGE;
}

// Fiat-Symbol-Set fuer Swap->Kauf/Verkauf-Normalisierung. Stablecoins (USDC,
// USDT, DAI, etc.) sind NICHT enthalten -- die bleiben Crypto-zu-Crypto-Swaps.
const NORM_FIAT_SET = new Set([
  'EUR', 'USD', 'GBP', 'CHF', 'AUD', 'CAD', 'JPY', 'TRY', 'BRL',
]);
